import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { Accounting, PayoutIntegrationStatus } from 'anchor-scout-common/types';
import { PayoutIntegrationStatusValue as ExternalPayoutIntegrationStatusValue } from 'anchor-scout-common/types/external-api';
import { IntegrationFailureModel } from './IntegrationFailureModel';
export class PayoutIntegrationModel {
    static fromDTO(dto) {
        if (dto.status === PayoutIntegrationStatus.SyncFailed) {
            return new PayoutIntegrationModel({
                status: dto.status,
                syncFailed: new IntegrationFailureModel(dto.syncFailed)
            });
        }
        return new PayoutIntegrationModel(dto);
    }
    static fromExternalDTO(status) {
        switch(status.status){
            case ExternalPayoutIntegrationStatusValue.PendingSync:
                return new PayoutIntegrationModel({
                    status: PayoutIntegrationStatus.PendingSync,
                    pendingSync: {
                        invoicesReady: status.pendingSync.allInvoicesSynced
                    }
                });
            case ExternalPayoutIntegrationStatusValue.Synced:
                return new PayoutIntegrationModel({
                    status: PayoutIntegrationStatus.Synced,
                    synced: {
                        date: new Date(status.synced.date).getTime()
                    }
                });
            case ExternalPayoutIntegrationStatusValue.SyncFailed:
                return new PayoutIntegrationModel({
                    status: PayoutIntegrationStatus.SyncFailed,
                    syncFailed: new IntegrationFailureModel({
                        date: new Date(status.syncFailed.date).getTime(),
                        customFailure: _object_spread_props(_object_spread({}, status.syncFailed.customFailure), {
                            code: status.syncFailed.customFailure.code
                        })
                    })
                });
            case ExternalPayoutIntegrationStatusValue.SyncInProgress:
                return new PayoutIntegrationModel({
                    status: PayoutIntegrationStatus.SyncInProgress
                });
            case ExternalPayoutIntegrationStatusValue.Deleted:
                return new PayoutIntegrationModel({
                    status: PayoutIntegrationStatus.Deleted,
                    deleted: {
                        date: new Date(status.deleted.date).getTime()
                    }
                });
        }
    }
    static fromExternalAccountingDTO(integrations) {
        const defaultPendingSync = ()=>new PayoutIntegrationModel({
                status: PayoutIntegrationStatus.PendingSync,
                pendingSync: {
                    invoicesReady: false
                }
            });
        return [
            {
                type: Accounting.QBO,
                integration: integrations.quickbooks != null ? PayoutIntegrationModel.fromExternalDTO(integrations.quickbooks) : defaultPendingSync()
            },
            {
                type: Accounting.Xero,
                integration: integrations.xero != null ? PayoutIntegrationModel.fromExternalDTO(integrations.xero) : defaultPendingSync()
            }
        ];
    }
    static fromAccountingDTO(dtos) {
        const existingIntegrations = dtos.map((dto)=>({
                type: dto.type,
                integration: PayoutIntegrationModel.fromDTO(dto.value)
            }));
        const hasQbo = existingIntegrations.some((integration)=>integration.type === Accounting.QBO);
        const hasXero = existingIntegrations.some((integration)=>integration.type === Accounting.Xero);
        const defaultIntegrations = [];
        if (!hasQbo) {
            defaultIntegrations.push({
                type: Accounting.QBO,
                integration: new PayoutIntegrationModel({
                    status: PayoutIntegrationStatus.PendingSync,
                    pendingSync: {
                        invoicesReady: false
                    }
                })
            });
        }
        if (!hasXero) {
            defaultIntegrations.push({
                type: Accounting.Xero,
                integration: new PayoutIntegrationModel({
                    status: PayoutIntegrationStatus.PendingSync,
                    pendingSync: {
                        invoicesReady: false
                    }
                })
            });
        }
        return [
            ...existingIntegrations,
            ...defaultIntegrations
        ];
    }
    isStableStatus() {
        return this.data.status !== PayoutIntegrationStatus.SyncInProgress;
    }
    isSyncFailed() {
        return this.data.status === PayoutIntegrationStatus.SyncFailed;
    }
    isSynced() {
        return this.data.status === PayoutIntegrationStatus.Synced;
    }
    status() {
        return this.data.status;
    }
    isPendingSync() {
        return this.data.status === PayoutIntegrationStatus.PendingSync;
    }
    date() {
        switch(this.data.status){
            case PayoutIntegrationStatus.SyncFailed:
                return new Date(this.data.syncFailed.date());
            case PayoutIntegrationStatus.Synced:
                return new Date(this.data.synced.date);
            case PayoutIntegrationStatus.Deleted:
                return new Date(this.data.deleted.date);
            case PayoutIntegrationStatus.PendingSync:
            case PayoutIntegrationStatus.SyncInProgress:
                return null;
        }
    }
    invoicesReady() {
        return this.data.status === PayoutIntegrationStatus.PendingSync ? this.data.pendingSync.invoicesReady : null;
    }
    accept(visitor) {
        switch(this.data.status){
            case PayoutIntegrationStatus.Synced:
                return visitor.whenSynced(this.data.synced);
            case PayoutIntegrationStatus.SyncFailed:
                return visitor.whenSyncFailed(this.data.syncFailed);
            case PayoutIntegrationStatus.PendingSync:
                return visitor.whenPendingSync(this.data.pendingSync);
            case PayoutIntegrationStatus.SyncInProgress:
                return visitor.whenSyncInProgress();
            case PayoutIntegrationStatus.Deleted:
                return visitor.whenDeleted(this.data.deleted);
        }
    }
    constructor(data){
        _define_property(this, "data", void 0);
        this.data = data;
    }
}
